import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { url } from '../../../../env';
import { AppButton, AppSelectField, AppTextareaField } from '../../../../Components/FormKit';
import usePermissions from '../../../../Hooks/usePermissions';
import { roleChipCSS } from './UsersManagement.css';
import { ALLOWED_TRANSITIONS, ROLE_DESCRIPTIONS, ROLE_OPTIONS, labelOf } from './roleConfig';

const UserRoleTab = ({ user, toast, onUpdate }) => {
    const { isAdminPei } = usePermissions();

    const [newRole, setNewRole] = useState(null);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const allowed = useMemo(() => {
        const transitions = ALLOWED_TRANSITIONS[user?.type_user] ?? [];
        return ROLE_OPTIONS.filter((o) => transitions.includes(o.value));
    }, [user?.type_user]);

    const noTransitions = allowed.length === 0;

    const canSubmit = isAdminPei() && newRole && reason.trim().length >= 10 && !saving;

    const onChangeRole = async () => {
        if (!canSubmit) return;
        setSaving(true);
        try {
            await axios.put(
                `${url}/users/${user.id}/role`,
                { type_user: newRole, role_change_reason: reason.trim() },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            toast?.current?.show({
                severity: 'success',
                summary: 'Rol actualizado',
                detail: `${user.name} ahora es ${labelOf(newRole)}.`,
                life: 3000,
            });
            setNewRole(null);
            setReason('');
            onUpdate?.();
        } catch (e) {
            console.error(e);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: e?.response?.data?.message ?? 'No se pudo cambiar el rol.',
                life: 4000,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <h3 className="udm-section-title">Rol actual</h3>
            <div className="udm-current-role">
                <div>
                    <span css={roleChipCSS(user?.type_user)} style={{ fontSize: '0.78rem', padding: '5px 14px' }}>
                        {labelOf(user?.type_user)}
                    </span>
                </div>
                <p className="udm-current-role-desc" style={{ margin: 0 }}>
                    {ROLE_DESCRIPTIONS[user?.type_user ?? 'null']}
                </p>
            </div>

            {isAdminPei() && (
                <>
                    <h3 className="udm-section-title">Cambiar rol</h3>

                    <div className="udm-callout">
                        <i className="pi pi-info-circle" />
                        <span>
                            Solo se permiten transiciones dentro de la misma plataforma
                            (Pei ↔ Pei, Trim ↔ Trim).
                        </span>
                    </div>

                    {noTransitions ? (
                        <p style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic' }}>
                            Este usuario no tiene transiciones de rol disponibles.
                        </p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                                <AppSelectField
                                    label="Nuevo rol"
                                    value={newRole}
                                    options={allowed}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Selecciona un rol"
                                    onChange={(e) => setNewRole(e.value)}
                                />

                                <AppTextareaField
                                    label="Motivo del cambio"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    placeholder="Describe brevemente el motivo del cambio (mínimo 10 caracteres)"
                                    helperText={`${reason.trim().length}/10 caracteres mínimos`}
                                    required
                                />
                            </div>

                            <div className="udm-footer" style={{ justifyContent: 'flex-end' }}>
                                <AppButton
                                    label="Cambiar rol"
                                    variant="primary"
                                    onClick={onChangeRole}
                                    disabled={!canSubmit}
                                    loading={saving}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default UserRoleTab;
